<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/org_invoice.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/agro_exporter/finances/harvested_batch_outcomes/show_process.yml"
  lang="yaml"
></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_processes')"
    :params="{ agro_exporter_id: agro_exporter_id }"
  >
  </go-back-link>

  <bread-crumbs :labels="[module_name, agro_exporter_process.name]" />

  <template v-if="load_data_general">
    <div class="mt-6 flex flex-row justify-between">
      <div class="panel-plain-label space-x-4">
        <badge-default
          :label="
            org_invoices.length + ' ' + $t('labels.total_number_org_invoices')
          "
        />
        <badge-default
          :label="
            org_invoices_paid +
            ' ' +
            $t('labels.total_number_org_invoices_paid')
          "
        />
      </div>
      <div class="flex gap-x-2">
        <button
          type="button"
          @click.prevent="downloadReport()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading ? 'fa-sync fa-spin' : 'fa-file-excel',
            ]"
          ></i
          ><span class="space-icon"></span>{{ $t("labels.download_button") }}
        </button>
        <new-button
          @click.prevent="goToNewInvoice()"
          :label="$t('labels.new_button')"
        />
      </div>
    </div>

    <div class="mt-2 flex flex-col">
      <ag-grid-vue
        style="height: 650px"
        class="ag-theme-alpine"
        @grid-ready="onGridReady"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef()"
        :rowData="org_invoices"
      >
      </ag-grid-vue>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/finances/harvested_batch_outcomes/show_process.js";
import ManageRequester from "@/views/agro_exporter/requesters/finances/harvested_batch_outcomes/manage_org_invoice.js";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";

import PayOrgInvoiceButton from "@/components/views/agro_exporter/finances/harvested_batch_outcomes/PayOrgInvoiceButton.vue";
import YesNoLabel from "@/components/views/layout/ag_grid/YesNoLabel.vue";
import {
  dateFilter,
  booleanFilter,
} from "@/components/views/mixins/ag_grid_filters.js";
export default {
  name: "OrgFinancesContainerIncomesShowProcess",
  components: {
    AgGridVue,
  },
  props: {
    agro_exporter_process_id: { required: true },
    agro_exporter_id: { required: true },
  },
  data() {
    return {
      load_data_general: false,
      downloading: false,
      agro_exporter_process: {},
      org_invoices: [],

      columnDefs: [
        {
          headerName: this.$t("model.org_invoice.week"),
          field: "week",
          filter: "agNumberColumnFilter",
          flex: 1,
        },
        {
          headerName: this.$t("model.org_invoice.invoice_date"),
          valueGetter: (params) => {
            return Date.parse(params.data.invoice_date);
          },
          valueFormatter: (params) => {
            return this.dateFormatter(params.data.invoice_date);
          },
          filter: "agDateColumnFilter",
          filterParams: dateFilter,
          flex: 1,
        },
        {
          headerName: this.$t("model.org_invoice.invoice_code"),
          field: "invoice_code",
          filter: true,
          flex: 1,
          cellRendererSelector: (params) => {
            const agro_exporter_id = this.agro_exporter_id;
            const agro_exporter_process_id = this.agro_exporter_process_id;
            const GoToLink = {
              template: `<span @click.prevent="this.showExportInvoice()" class="link">${params.value}</span>`,
              methods: {
                showExportInvoice: function () {
                  this.$router.push({
                    name: this.routeName("show_invoice"),
                    params: {
                      agro_exporter_id: agro_exporter_id,
                      agro_exporter_process_id: agro_exporter_process_id,
                      org_invoice_id: params.data.id,
                    },
                  });
                },
              },
            };
            return {
              component: GoToLink,
            };
          },
        },
        {
          headerName: this.$t("model.org_invoice.invoice_issuer"),
          field: "invoice_issuer",
          filter: true,
          flex: 2,
        },
        {
          headerName: this.$t("model.org_invoice.amount"),
          field: "amount",
          filter: "agNumberColumnFilter",
          flex: 1,
          valueFormatter: (params) => {
            return this.currencyMoney(
              params.value,
              params.data.currency.symbol
            );
          },
        },
        {
          headerName: this.$t("model.org_invoice.detraction_total"),
          field: "detraction_total",
          filter: "agNumberColumnFilter",
          flex: 1,
          valueFormatter: (params) => {
            return `${this.currencyMoney(
              params.value,
              params.data.currency.symbol
            )}
            (${this.numberWithPrecision(
              Number(params.data.detraction_percent) * 100,
              2
            )}%)
            `;
          },
        },
        {
          headerName: this.$t("model.org_invoice.paid"),
          field: "paid",
          filter: "agNumberColumnFilter",
          filterParams: booleanFilter,
          flex: 1,
          cellRenderer: YesNoLabel,
        },
        {
          headerName: "",
          filter: false,
          flex: 1,
          cellRendererSelector: (params) => {
            const payOrgInvoice = this.updateExportInvoice;
            const PayButton = {
              template: PayOrgInvoiceButton.template,
              data() {
                return { paid: params.data.paid };
              },
              methods: {
                payOrgInvoice: async function () {
                  payOrgInvoice(params.data.id);
                },
              },
            };
            return { component: PayButton };
          },
        },
      ],
    };
  },
  computed: {
    org_invoices_paid: function () {
      return this.org_invoices.filter((org_invoice) => org_invoice.paid).length;
    },
  },
  methods: {
    loadData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchOrgInvoices();
      this.load_data_general = true;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },

    fetchOrgInvoices: async function () {
      let response = await Requester.fetchOrgInvoices(
        this.agro_exporter_id,
        this.agro_exporter_process_id
      );
      this.org_invoices = response.data;
    },

    updateExportInvoice: async function (org_invoice_id) {
      let org_invoice = this.org_invoices.find(
        (org_invoice) => org_invoice.id == org_invoice_id
      );
      try {
        let response = await ManageRequester.update(
          this.agro_exporter_id,
          org_invoice.id,
          {
            paid: !org_invoice.paid,
          }
        );
        let org_invoice_update = response.data.org_invoice;
        org_invoice.paid = org_invoice_update.paid;
        org_invoice.amount_paid = org_invoice_update.amount_paid;
        org_invoice.amount_balance = org_invoice_update.amount_balance;
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
    },

    downloadReport: async function () {
      this.downloading = true;
      try {
        var response = await Requester.downloadRepXlsx(
          this.agro_exporter_id,
          this.agro_exporter_process_id
        );
        const file = response.data;
        this.downloadFile(
          file,
          `Facturas ${this.agro_exporter_process.name}.xlsx`
        );
      } catch (error) {
        error;
      }
      this.downloading = false;
    },

    goToNewInvoice: function () {
      this.$router.push({
        name: this.routeName("new_invoice"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          agro_exporter_process_id: this.agro_exporter_process_id,
        },
      });
    },

    defaultColDef() {
      return {
        sortable: true,
        filter: true,
        floatingFilter: true,
      };
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.gridApi.sizeColumnsToFit({ defaultMinWidth: 150 })
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id,
        },
      }).then(() => this.loadData())
    );
  },
};
</script>
